import {
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconNumber1,
  IconNumber2,
  IconNumber3,
  IconNumber4,
  IconToolsKitchen2,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  MARKETING_HOW_IT_WORKS_ROLE_IDS,
  MARKETING_HOW_IT_WORKS_STEP_IDS,
} from '#marketing/lib/marketing-capabilities.js';
import { localePath } from '#marketing/lib/marketing-locale.js';
import { useMarketingLocale } from '#marketing/lib/use-marketing-locale.js';
import { MarketingSection } from './MarketingSection.jsx';

const STEP_ICONS = [IconNumber1, IconNumber2, IconNumber3, IconNumber4];

/** @type {Record<string, import('@tabler/icons-react').Icon>} */
const ROLE_ICONS = {
  parents: IconUsers,
  executives: IconUsersGroup,
  coordinators: IconToolsKitchen2,
};

/**
 * Public /how-it-works page.
 */
export function MarketingHowItWorksPage() {
  const { t } = useTranslation('common');
  const locale = useMarketingLocale();

  return (
    <Container size="lg" py="xl">
      <MarketingSection
        titleKey="marketing.howItWorks.heroTitle"
        descriptionKey="marketing.howItWorks.heroDescription"
      />
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg" mb="xl">
        {MARKETING_HOW_IT_WORKS_STEP_IDS.map((stepId, index) => {
          const StepIcon = STEP_ICONS[index] ?? IconNumber1;
          return (
            <Paper key={stepId} withBorder p="md" radius="md" bg="white">
              <Group wrap="nowrap" align="center" gap="sm" mb="xs">
                <ThemeIcon color="pachubColourScheme" size={36} radius="xl">
                  <StepIcon size={18} />
                </ThemeIcon>
                <Text fw={600} lh="snug">
                  {t(`marketing.howItWorks.steps.${stepId}.title`)}
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                {t(`marketing.howItWorks.steps.${stepId}.description`)}
              </Text>
            </Paper>
          );
        })}
      </SimpleGrid>

      <Title order={2} ta="center" mb="lg">
        {t('marketing.howItWorks.rolesTitle')}
      </Title>
      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg" mb="xl">
        {MARKETING_HOW_IT_WORKS_ROLE_IDS.map((roleId) => {
          const RoleIcon = ROLE_ICONS[roleId] ?? IconUsers;
          return (
            <Paper key={roleId} withBorder p="md" radius="md" bg="white">
              <Group wrap="nowrap" align="center" gap="sm" mb="xs">
                <ThemeIcon color="pachubColourScheme" size={36} radius="md" variant="light">
                  <RoleIcon size={20} />
                </ThemeIcon>
                <Text fw={600} lh="snug">
                  {t(`marketing.howItWorks.roles.${roleId}.title`)}
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                {t(`marketing.howItWorks.roles.${roleId}.description`)}
              </Text>
            </Paper>
          );
        })}
      </SimpleGrid>

      <Stack gap="lg">
        <Paper withBorder radius="md" p="xl" ta="center">
          <Text fw={700} size="lg" mb="sm">
            {t('marketing.howItWorks.ctaTitle')}
          </Text>
          <Group justify="center">
            <Button component={Link} to={localePath(locale, '/features')} color="pachubColourScheme">
              {t('marketing.howItWorks.ctaButton')}
            </Button>
          </Group>
        </Paper>

        <Paper withBorder radius="md" p="xl" bg="pachubColourScheme.0" ta="center">
          <Text fw={700} size="lg" mb="sm">
            {t('marketing.howItWorks.pilotCtaTitle')}
          </Text>
          <Text c="dimmed" mb="lg">
            {t('marketing.howItWorks.pilotCtaDescription')}
          </Text>
          <Group justify="center">
            <Button component={Link} to={localePath(locale, '/contact')} color="pachubColourScheme">
              {t('marketing.howItWorks.pilotCtaButton')}
            </Button>
          </Group>
        </Paper>
      </Stack>
    </Container>
  );
}
